import type { FC, ReactNode } from 'react';
import { VipIcon } from '@/app/components/icons/VipIcon';

import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { Select, SelectContent, SelectItem, SelectTriggerWithoutIcon, SelectValue } from '@/app/components/ui/select';
import { useIsScreen } from '@/app/hooks';
import { cn } from '@/app/utils';
import { CardRadioGroup } from './CardRadioGroup';

interface OptionItem {
  label: ReactNode;
  value: string;
  showVipIcon?: boolean;
}

interface SettingsSelectProps {
  title: ReactNode;
  value: string;
  onChange: (value: string) => void;
  options: OptionItem[];
  icon?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  width?: string;
}

export const SettingsSelect: FC<SettingsSelectProps> = ({
  title,
  value,
  onChange,
  options,
  icon,
  open,
  onOpenChange,
  width,
}) => {
  const { isSmScreen } = useIsScreen();

  if (isSmScreen) {
    return (
      <div className="mb-6">
        <p className="mb-3 text-sm text-system-white48">{title}</p>
        <CardRadioGroup
          value={value}
          options={options.map((opt) => ({
            label: opt.label?.toString() ?? '',
            value: opt.value,
            showVipIcon: opt.showVipIcon,
          }))}
          onSelect={(value) => onChange(value)}
        />
      </div>
    );
  }

  return (
    <Select value={value} onValueChange={onChange} open={open} onOpenChange={onOpenChange}>
      <CustomTooltip content={title} arrowClassName="text-[#22252A]" contentClassName="bg-[#22252A]">
        <SelectTriggerWithoutIcon
          className="flex h-9 w-[104px] items-center justify-evenly gap-3 border border-transparent bg-system-white04 px-4 py-2 text-sm text-system-white hover:border-system-white12 hover:bg-system-white08"
          style={width ? { width: `${width}px` } : undefined}
        >
          <span className="shrink-0 text-base">{icon}</span>
          <span className="h-full w-px bg-system-white12" />
          <SelectValue />
        </SelectTriggerWithoutIcon>
      </CustomTooltip>
      <SelectContent
        className="w-[104px] rounded-12 border border-system-white08 bg-[rgba(34,37,42,0.64)] backdrop-blur-xl"
        style={width ? { width: `${width}px` } : undefined}
        side="top"
        sideOffset={8}
      >
        {options.map((opt) => (
          <SelectItem
            key={opt.value}
            value={opt.value}
            className="mb-1 rounded-8 focus:bg-system-white08 data-[state=checked]:bg-system-white08"
          >
            <div className="flex w-full items-center justify-center gap-1">
              <span
                className={cn(
                  opt.showVipIcon &&
                    'bg-gradient-to-b from-[#7280FF] via-[#8C97FF] to-[#DADFFF] bg-clip-text font-semibold text-transparent',
                )}
              >
                {opt.label}
              </span>
              {opt.showVipIcon && <VipIcon className="text-base" />}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
